import React, { useState, useEffect, memo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useAuthUserClaims, useAuthSession } from '@nbx/frontend-helpers/subscribe';
import UnverifiedText from 'components/shared/UnverifiedText';
import ErrorBoundary from 'components/shared/ErrorBoundary';
import Text from 'components/shared/Text';
import ControlledTabs from 'components/shared/ControlledTabs';
import BooleanField from 'components/shared/BooleanField';
import HollowSelect from 'components/trade/HollowSelect';
import Card from 'components/trade/Card';
import { isNullish } from 'helpers/objects';
import { compareComplexProps } from 'helpers/memo';
import MarketInformationText from 'components/shared/MarketInformationText';
import { useTakerFee } from 'helpers/hooks/takerFee';
import OrderFormLimit from './OrderFormLimit';
import OrderFormMarket from './OrderFormMarket';
import styles from './styles.module.scss';

const SIDES = ['buy', 'sell'];
const ORDER_TYPES = ['limit', 'market'];

function OrderForms(props) {
  const session = useAuthSession();
  const claims = useAuthUserClaims(session?.userId);
  const [idVerified, setIDVerified] = useState(true);
  const [idVerificationInProgress, setIDVerificationInProgress] = useState(false);
  const [totpEnabled, setTOTPEnabled] = useState(true);
  const marketId = props.markets.data?.markets?.id;
  const [takerFee, updateTakerFee] = useTakerFee(marketId);
  const { t } = useTranslation();

  const sideTabs = [{ label: t('common_buy') }, { label: t('common_sell') }];
  const sideBoolean = [
    { label: t('common_buy'), value: 0 },
    { label: t('common_sell'), value: 1 }
  ];
  const orderTypeTabs = [{ label: t('common_limit_order') }, { label: t('common_market_order') }];
  const orderTypeOptions = { 0: t('common_limit_order'), 1: t('common_market_order') };

  const [orderSide, setOrderSide] = useState(0);
  const [orderType, setOrderType] = useState(0);

  useEffect(() => {
    setTOTPEnabled(claims?.roles ? claims.roles.includes('TOTP') : true);
    setIDVerified(claims?.roles ? claims.roles.includes('KYC') : true);
    setIDVerificationInProgress(claims?.roles ? claims.roles.includes('KYC-ACCEPTED') : false);
  }, [claims]);

  useEffect(() => {
    updateTakerFee();
  }, [props.markets, updateTakerFee]);

  const isVerified = idVerified && totpEnabled;

  const handleSwitchSide = side => {
    setOrderSide(parseInt(side, 10));
    props.onChangeSide(side, orderType);
  };

  const handleSwitchType = type => {
    setOrderType(type);
    props.onChangeType(type, orderSide);
  };

  const refetchOnError = () => {
    if (props.trades.error) props.trades.refetch();
    if (props.markets.error) props.markets.refetch();
    if (props.funds.error) props.funds.refetch();
  };

  const loading = props.markets.loading || props.funds.loading || props.trades.loading;
  const error = props.markets.error || props.funds.error || props.trades.error;
  const needOrderBookAndIsLoading =
    ORDER_TYPES[orderType] === 'market' && (props.trades.loading || props.orderBook.loading);
  const needOrderBookAndIsBroken = ORDER_TYPES[orderType] && (props.trades.error || props.orderBook.error);

  const renderMobileControls = () => {
    if (!props.isMobile) return null;
    return (
      <>
        <BooleanField
          id="formSide"
          name="Buy or Sell?"
          options={sideBoolean}
          value={orderSide}
          onChange={e => handleSwitchSide(+e.target.value)}
          hasSelection
          wrapperStyles={[styles.mobileControlWrapper]}
          labelStyles={[styles.hiddenLabel]}
        />
        <HollowSelect
          id="orderType"
          name="Order Type"
          options={orderTypeOptions}
          value={orderType}
          onChange={e => handleSwitchType(+e.target.value)}
          wrapperStyles={[styles.typeSelectorMobile]}
        />
      </>
    );
  };

  const renderForm = () => {
    if (!isVerified && !!session?.userId) {
      return (
        <div className={classnames([styles.prompt, styles.unverifiedPrompt])}>
          <UnverifiedText
            idVerified={idVerified}
            idVerificationInProgress={idVerificationInProgress}
            totpEnabled={totpEnabled}
            isMobile={props.isMobile}
          />
        </div>
      );
    }

    if (props.markets.data?.markets?.cancelOnly) {
      return <MarketInformationText text={t('market_orders_cancel_only', { pair: props.pair })} />;
    }

    if (props.markets.data?.markets?.disabled) {
      return <MarketInformationText text={t('market_is_disabled', { pair: props.pair })} />;
    }

    if (ORDER_TYPES[orderType] === 'limit') {
      return (
        <>
          {renderMobileControls()}
          <OrderFormLimit
            side={SIDES[orderSide]}
            pair={props.pair}
            userId={session?.userId}
            market={props.markets.data.markets}
            funds={props.funds.data}
            selectedOrder={props.selectedOrder && props.selectedOrder.order}
            onSuccess={props.onSuccess}
            takerFee={takerFee}
          />
        </>
      );
    }

    if (!props.markets.data.markets.limitOnly) {
      let noPriceNoLiquidity = false;
      if (
        SIDES[orderSide] === 'buy' &&
        isNullish(props.trades.data.marketPrice) &&
        props.orderBook?.sells?.length === 0
      )
        noPriceNoLiquidity = true;
      else if (
        SIDES[orderSide] === 'sell' &&
        isNullish(props.trades.data.marketPrice) &&
        props.orderBook?.buys?.length === 0
      )
        noPriceNoLiquidity = true;

      if (noPriceNoLiquidity) {
        return (
          <>
            {renderMobileControls()}
            <div className={classnames([styles.prompt, styles.unverifiedPrompt])}>
              <Text tall centered>
                {t('market_cannot_place_market_order', {
                  side: SIDES[orderSide] === 'buy' ? t('common_buy') : t('common_sell'),
                  pair: props.pair.split('-').join(' / ')
                })}
              </Text>
            </div>
          </>
        );
      }

      return (
        <>
          {renderMobileControls()}
          <OrderFormMarket
            side={SIDES[orderSide]}
            pair={props.pair}
            userId={session?.userId}
            market={props.markets.data.markets}
            funds={props.funds.data}
            orderBook={props.orderBook}
            trades={props.trades}
            selectedOrder={props.selectedOrder && props.selectedOrder.order}
            onSuccess={props.onSuccess}
            isMobile={props.isMobile}
            takerFee={takerFee}
          />
        </>
      );
    }

    return (
      <>
        {renderMobileControls()}
        <div className={styles.prompt}>
          <Text centered>{t('market_orders_disabled', { pair: props.pair })}</Text>
        </div>
      </>
    );
  };

  return (
    <Card
      cardStyles={[styles.card]}
      contentWrapperStyles={[styles.cardContent]}
      customHeader={
        <div className={styles.header}>
          <ControlledTabs
            tabs={sideTabs}
            activeTabIndex={orderSide}
            setActiveTabIndex={handleSwitchSide}
            rowStyles={[styles.sideTabRow]}
            buttonStyles={[styles.buttonTabButton]}
            border={false}
            indicator={false}
          />
          <div className={styles.tabsContainer}>
            <ControlledTabs
              tabs={orderTypeTabs}
              activeTabIndex={orderType}
              setActiveTabIndex={handleSwitchType}
              rowStyles={[styles.textTabRow]}
              buttonStyles={[styles.textTabButton]}
              border={false}
              trading
            />
          </div>
        </div>
      }
      error={error || needOrderBookAndIsBroken}
      errorRefetch={refetchOnError}
      loading={loading || needOrderBookAndIsLoading}
      isMobile={props.isMobile}>
      <ErrorBoundary>{renderForm()}</ErrorBoundary>
    </Card>
  );
}

/* eslint-disable react/no-unused-prop-types */
OrderForms.propTypes = {
  pair: PropTypes.string.isRequired,
  markets: PropTypes.object.isRequired,
  funds: PropTypes.object.isRequired,
  trades: PropTypes.object.isRequired,
  orderBook: PropTypes.object.isRequired,
  selectedOrder: PropTypes.object.isRequired,
  onChangeSide: PropTypes.func.isRequired,
  onChangeType: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  isMobile: PropTypes.bool
};

OrderForms.defaultProps = {
  isMobile: false
};

export default memo(OrderForms, compareComplexProps);
